import React from 'react';
import { ArrowRightIcon, BedDoubleIcon, PhoneIcon, UserRoundIcon, PackageIcon, HomeIcon, MapPinIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { phones, isLive, siteConfig } from '../../data/siteConfig';
import type { StringKey } from '../../data/i18n';
import type { Bedrooms } from '../../types/funnel';
import { usd } from '../../utils/format';
import type { PropertyKey } from '../../data/propertyImages';
import { BrandPhoto } from './BrandPhoto';
import { PropertyPhoto } from './PropertyPhoto';
// Hero is intentionally minimal: headline, then the size cards that start the booking in one tap.
import { HelpAnswers } from './HelpAnswers';
import { PhotoGallery } from './PhotoGallery';
import { TrustSection } from './TrustSection';
const sizeCards: {
  bedrooms: Bedrooms;
  title: StringKey;
  hint: StringKey;
  beds: number;
  property: PropertyKey;
}[] = [{
  bedrooms: '1',
  title: 'card1',
  hint: 'crewHint2',
  beds: 1,
  property: 'apt_small'
}, {
  bedrooms: '2',
  title: 'card2',
  hint: 'crewHint23',
  beds: 2,
  property: 'apt_mid'
}, {
  bedrooms: '3',
  title: 'card3',
  hint: 'crewHint3',
  beds: 3,
  property: 'house_small'
}, {
  bedrooms: '4',
  title: 'card4',
  hint: 'crewHint3r',
  beds: 4,
  property: 'house_mid'
}];
export function HomeLanding() {
  const {
    t,
    lang,
    start,
    config
  } = useFunnel();
  // Reserve/sign/pay wording only once paid booking is live and tested; until then the copy describes what actually happens.
  const bookingLive = isLive() && siteConfig.depositCheckoutEnabled;
  return <>
      <section className="bg-paper" data-id="element-466" data-mp="1ved4hbrqzyac">
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-6 sm:px-6 lg:pb-14 lg:pt-10" data-id="element-467" data-mp="1qc5ma4gbrlzk">
          <h1 className="font-display text-[34px] font-black leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-468" data-mp="1mblt1byvh2ip">
            <span className="text-magenta" data-id="element-469" data-mp="3kpx5nvoghk87">{t('homeTitle1')}</span>
            <br data-id="element-470" data-mp="3gpmp5p31fbf3" />
            {t('homeTitle2')}
          </h1>
          <p className="mt-3 text-lg font-semibold text-ink-700 sm:text-xl" data-id="element-471" data-mp="3gq4q2z0nadtc">{t('homePick')}</p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4 lg:mt-7 lg:grid-cols-4" data-id="element-472" data-mp="1e1dbaqy28dbx">
            {sizeCards.map(c => <button key={c.bedrooms} type="button" onClick={() => start(c.bedrooms)} className="group flex flex-col overflow-hidden rounded-2xl border-2 border-ink-200 bg-white text-left transition-[border-color,transform] duration-150 ease-out hover:border-ink active:scale-[0.98]" data-id="element-473" data-mp="151zz3qijxani">
                <span className="relative block" aria-hidden="true" data-id="element-474" data-mp="21rqurt2kx3uw">
                  <PropertyPhoto property={c.property} lang={lang} priority decorative className="aspect-[4/3] w-full" data-id="element-475" data-mp="1nrg00ey5f54g" />
                  <span className="absolute bottom-2 left-2 flex items-center gap-0.5 rounded-md bg-white/95 px-1.5 py-1 text-lime-dark" data-id="element-476" data-mp="1sq10mfhbbqtf">
                    {Array.from({
                  length: c.beds
                }).map((_, i) => <BedDoubleIcon key={i} className="h-4 w-4" strokeWidth={2} data-id="element-477" data-mp="hdqgsykjr514" data-mpi={i} />)}
                  </span>
                </span>
                <span className="flex flex-1 items-center justify-between gap-2 p-3 sm:p-4" data-id="element-478" data-mp="4ucqxfb4ublu">
                  <span className="font-display text-base font-extrabold leading-tight text-ink sm:text-xl" data-id="element-479" data-mp="itchisi7h0kn">{t(c.title)}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-magenta text-white transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-480" data-mp="dvjajx92w2jb">
                    <ArrowRightIcon className="h-4 w-4" data-id="element-481" data-mp="3hct366599s5d" />
                  </span>
                </span>
              </button>)}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap" data-id="element-482" data-mp="3r9nx0bql34db">
            <button type="button" onClick={() => start('5')} className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 text-sm font-bold text-ink transition-[border-color] duration-150 ease-out hover:border-ink" data-id="element-483" data-mp="3r7kyt4fa24xi">
              <HomeIcon className="h-4 w-4 text-ink-500" aria-hidden="true" data-id="element-484" data-mp="21v6jivirr7fu" />
              {t('more5')}
            </button>
            <button type="button" onClick={() => start('few')} className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 text-sm font-bold text-ink transition-[border-color] duration-150 ease-out hover:border-ink" data-id="element-485" data-mp="26q2f25n7scxp">
              <PackageIcon className="h-4 w-4 text-ink-500" aria-hidden="true" data-id="element-486" data-mp="2kjlaxjx00nh6" />
              {t('fewItems')}
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-x-6 gap-y-2 text-sm sm:flex-row sm:flex-wrap sm:items-center" data-id="element-487" data-mp="2uhhk466dl8cn">
            <span className="flex items-center gap-1.5 font-medium text-ink-600" data-id="element-488" data-mp="2yjvyacz3njxl">
              <MapPinIcon className="h-4 w-4 shrink-0 text-lime-dark" aria-hidden="true" data-id="element-489" data-mp="104hvxonxj5tr" />
              {t('homeArea')}
            </span>
            <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-bold text-ink underline decoration-lime decoration-2 underline-offset-4 sm:min-h-0" data-id="element-490" data-mp="1463fttyvskdg">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-491" data-mp="ruarazc1hihh" />
              {t('preferTalk')} {phones.ai.display}
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-ink-200 bg-white" data-id="element-492" data-mp="iv787vcvh32z">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.4fr_1fr]" data-id="element-493" data-mp="isuigw0vhofd">
          <div data-id="element-494" data-mp="1fikbjwtlty6i">
            <h2 className="font-display text-2xl font-black text-ink" data-id="element-495" data-mp="1am3ay357xrcj">{t('howTitle')}</h2>
            <ol className="mt-6 grid gap-6 sm:grid-cols-3" data-id="element-496" data-mp="16k8at1z0xk0c">
              {[{
              k: 'how1' as StringKey,
              b: 'how1Body' as StringKey
            }, {
              k: 'how2' as StringKey,
              b: 'how2Body' as StringKey
            }, bookingLive ? {
              k: 'how3' as StringKey,
              b: 'how3Body' as StringKey
            } : {
              k: 'how3Preview' as StringKey,
              b: 'how3PreviewBody' as StringKey
            }].map((s, i) => <li key={s.k} className="border-t-2 border-ink pt-4" data-id="element-497" data-mp="3nst7kdu75rng" data-mpi={i}>
                  <span className="font-display text-sm font-black text-magenta" data-id="element-498" data-mp="3nqgp7zkj1f9v">{i + 1}</span>
                  <h3 className="mt-1 font-display text-lg font-extrabold leading-tight text-ink" data-id="element-499" data-mp="3spjwlqnzbxl0">{t(s.k)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink-600" data-id="element-500" data-mp="3eu7pskwjugnq">{t(s.b)}</p>
                </li>)}
            </ol>
            <BrandPhoto role="loading" alt="Our crew loading a truck" className="mt-8 aspect-[21/9] w-full rounded-2xl" icon={<UserRoundIcon className="h-10 w-10" strokeWidth={1.5} data-id="element-502" data-mp="39tk3udn6gq0d" />} data-id="element-501" data-mp="351sci0w76g96" />
          </div>
          <div className="space-y-10" data-id="element-503" data-mp="2v4xd7fs3obtu">
            <div className="rounded-2xl bg-ink p-6 text-white" data-id="element-504" data-mp="2v1t1lxcb2dt5">
              <h2 className="font-display text-xl font-extrabold" data-id="element-505" data-mp="3040fisgp43y4">{t('ratesTitle')}</h2>
              <dl className="mt-4 divide-y divide-ink-700" data-id="element-506" data-mp="2m2xxcqstndsh">
                {[2, 3].map(n => <div key={n} className="flex items-baseline justify-between py-3" data-id="element-507" data-mp="2r2kaz1ckaw1t">
                    <dt className="text-ink-200" data-id="element-508" data-mp="27fz5wvtysjdr">{t(n === 2 ? 'rate2' : 'rate3')}</dt>
                    <dd data-id="element-509" data-mp="22hnu4fk3zysw">
                      <span className="font-display text-3xl font-black text-lime" data-id="element-510" data-mp="22ifs1x16i8mc">{usd(config.rates[n], lang)}</span>
                      <span className="text-sm text-ink-300" data-id="element-511" data-mp="2ghyqtz83o7lz">{t('perHour')}</span>
                    </dd>
                  </div>)}
              </dl>
              <p className="mt-3 text-sm leading-relaxed text-ink-300" data-id="element-512" data-mp="2qetipz9vb9ra">{t('ratesNote')}</p>
            </div>
            <p className="border-l-4 border-lime pl-4 text-base font-medium leading-relaxed text-ink" data-id="element-513" data-mp="2lh1sd9ovfvp7">{bookingLive ? t('promise') : t('promisePreview')}</p>
            <HelpAnswers screen="home" data-id="element-514" data-mp="306zbhb7y1v7j" />
          </div>
        </div>
      </section>
      <TrustSection lang={lang} data-id="element-515" data-mp="2v6v2s7da24rv" />
      <PhotoGallery lang={lang} data-id="element-516" data-mp="395l2lurheijx" />
    </>;
}